import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { useI18n } from '../context/I18nContext';
import { publicApi } from '../api';
import { useApi } from '../hooks/useApi';
import { Spinner, EmptyState } from '../components/StateHelpers';
import { ArrowLeft, Calendar, User, Tag, Share2, Newspaper } from 'lucide-react';
import { staticImg } from '../utils/media';

export default function BlogDetail() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const nav = useNavigate();
  const { data: item, loading, error } = useApi(() => publicApi.blogPost(slug, lang), [slug, lang]);

  useEffect(() => {
    if (item?.title) {
      document.title = `${item.title} — Aegis Studios Blog`;
    }
  }, [item]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#08080b] text-[#e7e5e0] flex items-center justify-center">
        <Spinner />
      </main>
    );
  }

  if (error || !item) {
    return (
      <main className="min-h-screen bg-[#08080b] text-[#e7e5e0]">
        <Header />
        <div className="max-w-[1200px] mx-auto px-4 py-24 text-center">
          <EmptyState icon={Newspaper} title="Yazı Bulunamadı" subtitle="Aradığınız blog yazısı mevcut değil veya kaldırılmış olabilir." />
          <Link to="/blog" className="btn-gold h-10 px-6 rounded-lg inline-flex items-center gap-2 text-xs font-extrabold tracking-widest mt-6">
            <ArrowLeft className="w-4 h-4" /> BLOG'A DÖN
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#08080b] text-[#e7e5e0]">
      <Seo title={item.title} description={item.excerpt} image={item.image} schemaType="Article" />
      <Header />

      <article className="pt-28 pb-24">
        <div className="max-w-[900px] mx-auto px-4 md:px-6">
          <button onClick={() => nav('/blog')} className="h-9 px-4 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white text-xs font-bold inline-flex items-center gap-2 mb-8 transition">
            <ArrowLeft className="w-4 h-4" /> TÜM YAZILAR
          </button>

          {/* Meta Header */}
          <div className="space-y-4">
            {item.category_name && (
              <span className="px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest bg-[#e4b44e]/10 border border-[#e4b44e]/30 text-[#e4b44e]">
                {item.category_name}
              </span>
            )}
            <h1 className="font-display text-3xl md:text-5xl font-black text-white leading-tight">
              {item.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-xs text-white/50 pt-2 border-b border-white/8 pb-6">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-[#e4b44e]" />
                <span className="font-bold text-white/80">Aegis Ekibi</span>
              </div>
              {item.created_at && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#e4b44e]" />
                  <span>{item.created_at.slice(0, 10)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Featured Image */}
          {item.image && (
            <div className="my-8 rounded-2xl overflow-hidden border border-white/10 bg-[#0f0e12] aspect-video">
              <img src={staticImg(item.image)} alt={item.title} className="w-full h-full object-cover" />
            </div>
          )}

          {/* Article Body */}
          <div
            className="prose prose-invert max-w-none break-words text-white/80 prose-headings:font-display prose-headings:font-bold prose-headings:text-white prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-white/80 prose-p:leading-relaxed prose-p:mb-5 prose-p:break-words prose-a:text-[#e4b44e] prose-strong:text-white prose-code:text-[#e4b44e] prose-code:bg-white/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:break-words prose-pre:bg-[#050508] prose-pre:border prose-pre:border-white/10 prose-pre:p-5 prose-pre:rounded-xl prose-pre:overflow-x-auto prose-pre:text-sm prose-pre:leading-relaxed prose-img:rounded-xl prose-img:max-w-full mt-8"
            dangerouslySetInnerHTML={{ __html: (item.content || '').replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ') }}
          />

          {/* Bottom Share / CTA */}
          <div className="mt-16 pt-8 border-t border-white/10 flex items-center justify-between">
            <Link to="/blog" className="h-10 px-6 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:text-white text-xs font-bold inline-flex items-center gap-2 transition">
              <ArrowLeft className="w-4 h-4" /> Diğer Makaleleri İncele
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}
